<template>
    <div :id="componentId" class="dropdown"
         :ref="componentId"
         :style="{height: getHeight}"
         :class="{'flatStyle font-6': dropdownStyle === 'radio',
                  'font-5': dropdownStyle !== 'radio',
                  'dropdown-disabled': !options || isDisable}">
        <div class="dropDownField" @click="!isDisable && toggle()" :class="{withIcon: $slots.icon}">
            <div class="fullWidth truncate" :style="{'line-height': getHeight}">
                <slot name="icon">
                    <span v-if="enableIncludeExclude && selectedItems.length > 0" class="includeExcludeIcon">
                        <IconBase :iconName="`${isIncludeModel ? 'include' : 'block'}Icon`"
                                  :iconColor="consts.COLORS.PRIMARY_500"
                                  height="13"
                                  width="13"
                        ></IconBase>
                    </span>
                </slot>
                <span class="font-4" v-if="enableIncludeExclude && selectedItems.length > 0">
                    {{isIncludeModel ? 'Include' : 'Exclude'}}:
                </span>
                <Tooltip :text="current" class="fullWidth truncate current-tooltip" :style="{'line-height': getHeight}">
                    <span>{{current}}</span>
                </Tooltip>
            </div>
            <div v-if="!(dropdownStyle === 'radio' && isDisable)"
                 class="rightDropdownArrow"
                 :class="{'rightDropdownArrowOpen': menuOpen}"></div>
        </div>
        <div :id="'ssaCustomDropdownArea' + uniqueId" v-if="menuOpen" class="dropdownArea font-5"
             v-iron-source-click-outside="{
                    handler: 'closeMenu',
                    exclude: [componentId]
                 }"
        >
            <div v-if="!isTreeOptions">
                <div class="actionsWrap" v-if="searchShown || enableIncludeExclude"
                     :class="{'padding': actionsWrapPadding}">
                    <input type="search"
                           :ref="'search' + uniqueId"
                           class="search font-5"
                           placeholder="Search"
                           v-model="searchText"
                           v-if="searchShown"
                           v-once
                           @input="searchOptions(searchText)"
                           :id="`searchDropdown${uniqueId}`"
                    >
                    <div class="includeExclude font-5" v-if="enableIncludeExclude"
                         :class="{'no-search': !searchShown}">
                        <input type="radio" id="include"
                               v-model="isIncludeModel" :value="true"
                               @input="includeExcludeChanged">
                        <label for="include" class="include">Include</label>
                        <input type="radio" id="exclude"
                               v-model="isIncludeModel" :value="false"
                               @input="includeExcludeChanged">
                        <label for="exclude">Exclude</label>
                        <button class="button-linkStyle clearSelection" :id="`clearButtonDropdown${uniqueId}`"
                                @click="clearSelection">Clear</button>
                    </div>
                </div>
                <div v-if="searchShown && filteredOptions.length === 0"
                     class="font-4 searchNoResults">
                    No Results
                </div>
                <MultipleSelectionLeavesDropdown
                        :options="filteredOptions"
                        :selectedItems="selectedItems"
                        :idField="idField"
                        :displayField="displayField"
                        @dropdownSelectedOption="onMultipleSelectionClick"
                        :style="{'max-height': getMaxListHeight}"
                        v-if="isMultipleSelection"
                        :markSelected="markSelected">
                    <template slot="postOption" slot-scope="slotData">
                        <slot name="postOption" :option="slotData.option" />
                    </template>
                    <template slot="preOption" slot-scope="slotData">
                        <slot name="preOption" :option="slotData.option" />
                    </template>
                    <template slot="description" slot-scope="slotData">
                        <slot name="description" :option="slotData.option" />
                    </template>
                </MultipleSelectionLeavesDropdown>
                <leaves-dropdown :options="filteredOptions"
                                 :selected-item="selectedItem"
                                 :id-field="idField"
                                 :display-field="displayField"
                                 @dropdownSelectedOption="onClick"
                                 @closeMenu="closeMenu"
                                 class="single-select"
                                 :style="{'max-height': getMaxListHeight}"
                                 v-else-if="dropdownStyle === 'regular'"
                                 :markSelected="markSelected">
                    <template slot="postOption" slot-scope="slotData">
                        <slot name="postOption" :option="slotData.option" />
                    </template>
                    <template slot="preOption" slot-scope="slotData">
                        <slot name="preOption" :option="slotData.option" />
                    </template>
                    <template slot="description" slot-scope="slotData">
                        <slot name="description" :option="slotData.option" />
                    </template>
                </leaves-dropdown>
                <radio-leaves-dropdown :options="filteredOptions"
                                       :selected-item="selectedItem"
                                       :id-field="idField"
                                       :display-field="displayField"
                                       @dropdownSelectedOption="onClick"
                                       :optionsTitle="optionsTitle"
                                       class="single-select"
                                       :style="{'max-height': getMaxListHeight}"
                                       v-else-if="dropdownStyle === 'radio'">
                </radio-leaves-dropdown>
            </div>
            <div v-else-if="isTreeOptions">
                <tree-dropdown :options="options"
                               :selected-item="selectedItem"
                               :id-field="idField"
                               :display-field="displayField"
                               :selected-genre="selectedGenre"
                               @dropdownTreeSelectedOption="onTreeClick"
                               :get-max-list-height="getMaxListHeight"
                >
                </tree-dropdown>
            </div>
            <div class="footer" v-if="isMultipleSelection" v-once :class="{'padding': footerPadding}">
                <button type="button" class="secondary small" @click="closeMenu">Cancel</button>
                <button type="submit" class="primary small" :id="`submitButtonDropdown${uniqueId}`"
                        @click="applySelection">Apply</button>
            </div>
        </div>
    </div>
</template>

<script>
import consts from 'services/metaData/consts';
import IconBase from 'components/IconBase.vue';
import LeavesDropdown from './LeavesDropdown.vue';
import MultipleSelectionLeavesDropdown from './MultipleSelectionLeavesDropdown.vue';
import RadioLeavesDropdown from './RadioLeavesDropdown.vue';
import TreeDropdown from './TreeDropdown.vue';
import ironSourceClickOutside from '../../directives/IronSourceClickOutside';
import Tooltip from "components/Tooltip/Tooltip";

export default {
    name: 'Dropdown',
    components: {
        Tooltip,
        TreeDropdown, LeavesDropdown, RadioLeavesDropdown, MultipleSelectionLeavesDropdown, IconBase
    },
    data() {
        return {
            menuOpen: false,
            selectedItem: null,
            selectedItems: this.searchSelectedIds(),
            selectedGenreValue: '',
            searchText: '',
            filteredOptions: this.options,
            isIncludeModel: this.isInclude,
            consts
        };
    },
    props: {
        uniqueId: String,
        defaultText: String,
        options: {
            type: Array,
            required: true,
            default: () => []
        },
        isDisable: {
            type: Boolean,
            default: false
        },
        height: {
            type: [Number, String],
            default: 32
        },
        maxListHeight: {
            type: [Number, String],
            default: 270
        },
        selectedId: {
            type: [Number, String]
        },
        selectedIds: {
            type: Array,
            default: () => []
        },
        displayField: {
            type: String,
            default: 'displayText'
        },
        searchFields: {
            type: Array,
            default: () => []
        },
        idField: {
            type: String,
            default: 'id'
        },
        selectedDisplayTextField: {
            type: String,
            default: 'displayText'
        },
        dropdownStyle: {
            type: String,
            default: 'regular'
        },
        optionsTitle: {
            type: String,
            default: ''
        },
        enableIncludeExclude: {
            type: Boolean,
            default: false
        },
        isInclude: {
            type: Boolean,
            default: true
        },
        isMultipleSelection: {
            type: Boolean,
            default: false
        },
        selectionItemDescriptor: {
            type: String,
            default: ''
        },
        searchEnabled: {
            type: Boolean,
            default: true
        },
        footerPadding: Boolean,
        actionsWrapPadding: Boolean,
        markSelected: Boolean
    },
    computed: {
        componentId() {
            return `ssaCustomDropdownId${this.uniqueId}`;
        },
        getHeight() {
            return `${this.height - 2}px`;
        },
        getMaxListHeight() {
            return `${this.maxListHeight}px`;
        },
        current() {
            if (this.isMultipleSelection) {
                if (this.selectedItems.length === 0) {
                    return this.defaultItem[this.selectedDisplayTextField];
                }
                if (this.selectedItems.length === 1) {
                    const item = this.selectedItems[0];
                    if (item) {
                        return item[this.selectedDisplayTextField];
                    }
                }
                return `${this.selectedItems.length}
                    ${(this.selectionItemDescriptor ? `${this.selectionItemDescriptor} ` : ' ')}Selected`;
            }
            return (this.selectedItem || this.defaultItem || {})[this.selectedDisplayTextField];
        },
        defaultItem() {
            return { [this.idField]: '', [this.selectedDisplayTextField]: this.defaultText || '' };
        },
        isTreeOptions() {
            return this.options.some((option) => option.childOptions);
        },
        selectedGenre() {
            const defaultGenre = this.options.map(
                (category) => category.childOptions.map((genre) => genre.id)[0]
            )[0];

            return this.selectedGenreValue || defaultGenre || '';
        },
        searchShown() {
            return this.options.length > 8 && this.searchEnabled;
        }
    },
    created() {
        this.selectedItem = this.searchSelectedItem();
    },
    watch: {
        isDisable() {
            this.menuOpen = false;
        },
        selectedId() {
            this.selectedItem = this.searchSelectedItem();
        },
        selectedIds() {
            this.selectedItems = this.searchSelectedIds();
        },
        options() {
            this.searchOptions(this.searchText);
            this.selectedItem = this.searchSelectedItem();
            this.selectedItems = this.searchSelectedIds();
        },
        isInclude() {
            this.isIncludeModel = this.isInclude;
        }
    },
    methods: {
        clearSelection() {
            this.selectedItems = [];
            this.isIncludeModel = true;
            this.includeExcludeChanged();
        },
        closeMenu() {
            this.menuOpen = false;
            this.isIncludeModel = this.isInclude;
            this.selectedItems = this.searchSelectedIds();
            this.searchText = '';
            this.filteredOptions = this.options;
        },
        toggle() {
            this.menuOpen = !this.menuOpen;
            if (!this.menuOpen) {
                this.searchText = '';
                this.closeMenu();
            }

            this.$nextTick().then((() => {
                if (this.searchShown && this.menuOpen) {
                    this.$refs[`search${this.uniqueId}`].focus();
                }
                // eslint-disable-next-line no-extra-bind
            }).bind(this));
        },
        onClick(selectedOption) {
            this.applySingleSelection(selectedOption);
            this.toggle();
        },
        onMultipleSelectionClick({ option, isCheck }) {
            if (isCheck) {
                this.selectedItems.push(option);
            } else {
                this.selectedItems.splice(this.selectedItems.indexOf(option), 1);
            }
        },
        onTreeClick(selectedOption) {
            const { event, selectedGenre } = selectedOption;
            this.selectedGenreValue = selectedGenre;
            this.applySingleSelection(event);
            this.toggle();
        },
        applySingleSelection(selectedItem) {
            if (selectedItem === this.selectedItem) {
                return;
            }

            this.selectedItem = selectedItem;
            this.$emit('dropdownSelectedItemChanged', { ...this.selectedItem });
        },
        applySelection() {
            this.$emit('selectionChanged', {
                selected: this.selectedItems.map((item) => ({ ...item })),
                isIncludeMode: this.isIncludeModel
            });
            this.toggle();
        },
        searchSelectedItem() {
            if (!this.isTreeOptions) {
                // eslint-disable-next-line eqeqeq
                return this.options.find((x) => x[this.idField] == this.selectedId);
            }

            const allLeaves = this.options.flatMap((x) => x.childOptions).flatMap((x) => x.childOptions);
            // eslint-disable-next-line eqeqeq
            return allLeaves.find((x) => x[this.idField] == this.selectedId);
        },
        searchSelectedIds() {
            return this.options.filter((x) => this.selectedIds.includes(x[this.idField]));
        },
        searchOptions(value) {
            if (value) {
                const searchValue = value.toLowerCase();
                const searchFields = this.searchFields.length ? this.searchFields : [this.displayField];
                this.filteredOptions = this.options.filter(
                    (option) => option.isHeader
                        || searchFields.some((field) => option[field].toLowerCase().indexOf(searchValue) > -1)
                );
            } else {
                this.filteredOptions = this.options;
            }
        },
        includeExcludeChanged() {
            if (!this.isMultipleSelection) {
                this.$emit('includeExcludeChanged', this.isIncludeModel);
            }
        }
    },
    directives: {
        ironSourceClickOutside
    }
};
</script>

<style scoped lang="scss">
    @import '../../scss/styleGuide';
    @import '../../scss/buttons';

    $smallDropdownHeight: 25px;
    $mediumDropdownHeight: 32px;
    $largeDropdownHeight: 35px;

    $dropdownHeightField: $mediumDropdownHeight;
    $dropdownBorderBTWElements: $border;

    $dropdownItemHeight: 40px;
    $dropdownBorderBTWElements: $border;
    $paddingLeftElements: 18px;

    .dropdown {
        display: inline-block;
        width: 100%;
        height: $dropdownHeightField;
        min-width: 47px;
        position: relative;
        @include inputGradient;
        border-radius: $borderRadius;
        border: $border;

        &.dropdown-disabled {
            .dropDownField {
                cursor: default;
                color: $disabledText;
            }

            .rightDropdownArrow:after {
                border-top-color: $disabledText;
            }
        }

        &.flatStyle {
            background: none;
            border: none;
            height: 17px;
            width: initial;
            padding-right: 18px;

            &.dropdown-disabled {
                padding-right: 10px;
                .dropDownField .truncate{
                    text-decoration: none;
                }
            }

            .dropDownField {
                padding-left: 0;
                max-width: 100%;
                line-height: 16px;
                @include vendor-prefix('user-select', none);
                color: $light-800;

                .truncate {
                    text-decoration: underline;
                    display: inline-block;
                    margin-top: -1px;
                }
            }
            .rightDropdownArrow {
                /* stylelint-disable-line */
                background: transparent url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%228%22%20height%3D%225%22%20viewBox%3D%220%200%209%205%22%3E%0A%20%20%20%20%3Cpath%20fill%3D%22%23678099%22%20fill-rule%3D%22evenodd%22%20d%3D%22M1.444%200L4.5%203.107%207.556%200%208.5.955%204.5%205%20.5.955z%22%2F%3E%0A%3C%2Fsvg%3E%0A') no-repeat left top;
                margin-top: -3px;
                width: 8px;
                height: 5px;
                right: 6px;
                &:after {
                    content: none;
                }
            }

            .rightDropdownArrowOpen {
                transform: rotate(180deg);
            }

            .dropdownArea {
                margin-top: -2px;
                min-width: 164px;
                border-radius: $borderRadius;
            }
        }
    }

    .rightDropdownArrow { // the little arrow on the right
        text-align: center;
        display: block;
        position: absolute;
        width: 23px;
        height: inherit;
        top: 50%;
        right: 0;

        &:after {
            content: "";
            position: absolute;
            right: 50%;
            margin-right: -3px;
            margin-top: -2px;
            width: 0;
            height: 0;
            border-left: 4px solid transparent;
            border-right: 4px solid transparent;
            border-top: 4px solid $dark-300;
        }
    }

    .dropDownField {
        line-height: calc(100% - 2px);
        padding-left: 12px;
        height: calc(100% - 2px);
        cursor: pointer;
        width: 100%;
        max-width: calc(100% - 23px);
        .iconSection{
            margin-right: 5px;
        }

        &.withIcon {
            padding-left: 8px;

            .current-tooltip {
                width: calc(100% - 30px);
                display: initial;
                vertical-align: baseline;
            }
        }

        .current-tooltip {
            display: block;
        }

        .truncate {
            vertical-align: middle;
            line-height: 100%;
        }
    }

    .dropdownArea {
        @include fadeIn;
        background-color: $light-100;
        color: $dark-300;
        @include applicationLayer;
        position: absolute;
        border-top: $border;
        box-shadow: 0 3px 10px 0 rgba(77,80,95,.3);
        border-radius: $borderRadius;
        min-width: 100%;
        max-width: 480px;
        width: auto;
        margin-top: 2px;
    }

    .search {
        background-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2038%2038%22%3E%0A%20%20%20%20%3Cpath%20fill%3D%22%2395a6b8%22%20fill-rule%3D%22nonzero%22%20d%3D%22M15.155%200C6.777%200%200%206.621%200%2014.808s6.777%2014.808%2015.155%2014.808c2.992%200%205.76-.857%208.107-2.314L34.212%2038%2038%2034.298%2027.19%2023.764c1.944-2.491%203.12-5.577%203.12-8.956C30.31%206.621%2023.535%200%2015.156%200zm0%203.484c6.418%200%2011.59%205.053%2011.59%2011.324s-5.172%2011.324-11.59%2011.324S3.566%2021.079%203.566%2014.808c0-6.27%205.171-11.324%2011.59-11.324z%22/%3E%0A%3C/svg%3E");
        background-size: 15px 15px;
        background-repeat: no-repeat;
        background-position: center right 10px;
        padding-right: 25px;
        -webkit-appearance: textfield; // safari input-type:search fix
        margin: 0;
        width: 100%;
        min-width: 140px;
        border-radius: $borderRadius;
        line-height: 35px;
        height: 35px;
        border: $border;
        padding-left: 10px;
        background-color: $white;
        outline: none;

        /* clears the 'X' from Internet Explorer */
        &::-ms-clear {
            display: none;
            width : 0;
            height: 0;
        }
        &::-ms-reveal {
            display: none;
            width : 0;
            height: 0;
        }

        /* clears the 'X' from Chrome */
        &::-webkit-search-decoration,
        &::-webkit-search-cancel-button,
        &::-webkit-search-results-button,
        &::-webkit-search-results-decoration {
            display: none;
        }


        &:focus {
            border: solid 1px $primaryColor;
        }

        &[disabled] {
            background-color: $light-200;
            color: $light-800;
        }

        &::-webkit-input-placeholder, &:-moz-placeholder, &::-moz-placeholder, &:-ms-input-placeholder {
            color: $light-800;
        }

        &:-webkit-autofill {
            -webkit-box-shadow: 0 0 0 1000px $white inset;
        }
    }

    .actionsWrap {
        padding: 8px;
        background-color: $white;
        box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.1);
        margin-bottom: 2px;

        &.padding {
            padding-bottom: 20px;
        }
    }

    .searchNoResults {
        color: $light-800;
        padding: 13px 20px 8px 20px;
        white-space: nowrap;
        background-color: $white;
    }

    .clearSelection {
        color: $light-700;
        min-width: auto;
        justify-self: end;

        &:hover{
            color: $light-800;
        }

        &:active {
            min-width: inherit;
        }

        &.button-linkStyle {
            @include font(6);
            height: 18px;
        }
    }

    .includeExclude {
        display: grid;
        grid-template-columns: 60px 60px auto;
        height: 18px;
        padding-top: 10px;
        &.no-search {
            padding-top: 7px;
        }

        .include {
            border-right: $border;
        }
        input[type='radio'] {
            display: none;
            & + label{
                font-weight: 600;
                color: $dark-300;
                text-align: center;
                width: 100%;
                height: 18px;
                line-height: 13px;
                margin-left: 0;
                padding-top: 3px;
                cursor: pointer;
            }
            &:checked + label {
                color: $primaryColor;
                margin-left: 0;
                padding-top: 3px;
            }
        }
    }

    .footer {
        display: grid;
        grid-template-columns: 80px 80px;
        justify-content: end;
        height: 37px;
        padding-right: 12px;
        padding-top: 13px;
        border-top: $border;
        button {
            margin-left: 10px;
        }
        &.padding {
            padding-bottom: 35px;
        }
    }

    .includeExcludeIcon {
        vertical-align: middle;
        margin-right: 3px;
    }
</style>
